import { queryBuilder } from '@inpart-io/devtools-iap';
import { RECEIVE_DATA_OBJECTS, RETRIEVE_BY_QUERY, STORE_DATA_OBJECTS, UPDATE_DATA_OBJECT } from '@inpart-io/devtools-vue';
import { agreementsManager, DataType } from '@inpart-io/padme-iap';
import {
    AgreementCategoryValues,
    agreementDetailProperties,
    agreementDocumentProperties,
    agreementProperties,
    type IAgreement,
    type IAgreementDetail,
    type IAgreementParameters,
    type IExternalLink
} from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { endOfDay, getDateTime, getTimeStamp } from '@/services/date-format';

import { AGREEMENT_DETAIL_MODULE } from '../agreement-detail-module';
import { AGREEMENT_DOCUMENT_MODULE } from '../agreement-document-module';
import { UPLOAD_FILE_TO_OBJECT } from '../base/binary-uploads/actions';
import { RECEIVE_MOST_RECENT_CDA } from './mutations';

export const RETRIEVE_MOST_RECENT_CDA = 'retrieveMostRecentCDA';
export const CREATE_AGREEMENT = 'createAgreement';
export const ADD_LEADS_TO_AGREEMENT = 'addLeadsToAgreement';
export const REMOVE_LEADS_FROM_AGREEMENT = 'removeLeadsFromAgreement';

export const actions: ActionTree<IAgreement, unknown> = {
    async [CREATE_AGREEMENT](
        context,
        {
            agreement,
            alliances,
            link,
            document,
            relationships,
            properties
        }: { agreement: IAgreement; alliances: number[]; link?: IExternalLink; document?: File; relationships: number[]; properties?: string }
    ): Promise<number> {
        const params: IAgreementParameters = {
            properties
        };
        if (alliances?.length) {
            params.alliances = alliances.join(',');
        }
        if (relationships?.length) {
            params.relationships = relationships.join(',');
        }
        const newAgreement = await agreementsManager.createAgreementWithAttachment(agreement, link, params);
        await context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: [newAgreement] });
        if (document) {
            const newDocumentId = await context.dispatch(
                `${AGREEMENT_DOCUMENT_MODULE}/${UPLOAD_FILE_TO_OBJECT}`,
                {
                    objectId: (newAgreement['@detail'][0] as IAgreementDetail).dobjId,
                    objectType: DataType.AgreementDetail,
                    file: document,
                    properties: agreementDocumentProperties.DEFAULT
                },
                { root: true }
            );
            const newAgreementDetail = {
                dobjId: (newAgreement['@detail'][0] as IAgreementDetail).dobjId,
                mainAttachment: { dobjId: newDocumentId, type: DataType.AgreementDocument }
            };
            await context.dispatch(
                `${AGREEMENT_DETAIL_MODULE}/${UPDATE_DATA_OBJECT}`,
                {
                    dataObjectList: [newAgreementDetail],
                    properties: agreementDetailProperties.DEFAULT
                },
                { root: true }
            );
        }
        return newAgreement.dobjId;
    },

    async [RETRIEVE_MOST_RECENT_CDA](context, { leadId }: { leadId: number }) {
        const query = queryBuilder.build(
            queryBuilder.op.and(
                queryBuilder.cmp.isIn('leads', [leadId]),
                queryBuilder.cmp.equalTo('category', AgreementCategoryValues.CDA),
                queryBuilder.cmp.beforeOrEqualTo('@(:AgreementDetail:agreement).effectiveDate', getTimeStamp(endOfDay(getDateTime())))
            ),
            0,
            1,
            [
                { attribute: '@(:AgreementDetail:agreement).statusInfo.position', direction: 'ASC' },
                { attribute: '@(:AgreementDetail:agreement).effectiveDate', direction: 'DESC' }
            ]
        );

        const cdaId = (
            await context.dispatch(RETRIEVE_BY_QUERY, {
                query,
                properties: agreementProperties.DEFAULT
            })
        )?.[0];

        context.commit(RECEIVE_MOST_RECENT_CDA, { leadId, cdaId });
    },
    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: IAgreement[] }) {
        const agreements: IAgreement[] = [];
        const details: IAgreementDetail[] = [];

        dataObjectList.forEach((agreement) => {
            const _agreement = Object.assign({}, agreement);

            // Extract Details objects if necessary
            if ((agreement['@detail']?.length ?? 0) > 0) {
                agreement['@detail'].forEach((detail) => {
                    if (detail instanceof Object) {
                        details.push(Object.assign({}, detail, { '@type': DataType.AgreementDetail }));
                        _agreement.detail = detail.dobjId;
                    } else {
                        _agreement.detail = detail;
                    }
                });
            }
            _agreement['@detail'] = undefined;
            agreements.push(_agreement);
        });

        if (agreements.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: agreements });
        }
        if (details.length > 0) {
            void context.dispatch(`${AGREEMENT_DETAIL_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: details }, { root: true });
        }
    },
    async [ADD_LEADS_TO_AGREEMENT](
        context,
        { leadIdList, agreementId, properties }: { leadIdList: number[]; agreementId: number; properties?: string }
    ): Promise<number[]> {
        const agreementsToUpdate = {
            dobjId: agreementId,
            leads: { '@value': leadIdList, '@operation': 'ADD' }
        };
        return context.dispatch(UPDATE_DATA_OBJECT, { dataObjectList: [agreementsToUpdate], properties });
    },
    async [REMOVE_LEADS_FROM_AGREEMENT](
        context,
        { leadIdList, agreementId, properties }: { leadIdList: number[]; agreementId: number; properties?: string }
    ): Promise<number[]> {
        const agreementsToUpdate = {
            dobjId: agreementId,
            leads: { '@value': leadIdList, '@operation': 'REMOVE' }
        };
        return context.dispatch(UPDATE_DATA_OBJECT, { dataObjectList: [agreementsToUpdate], properties });
    }
};
